import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle, 
  Info,
  RefreshCw,
  Trash2,
  Calculator,
  Download,
  FileText,
  Mail,
  Scale,
  Loader2,
  Table as TableIcon
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import CCNLVerificationResult from '../components/CCNLVerificationResult';
import PayslipComparisonTable from '../components/PayslipComparisonTable';

import { API_URL } from '../config';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

export default function PayslipDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reverifying, setReverifying] = useState(false);
  
  // CCNL verification state
  const [ccnlList, setCcnlList] = useState([]);
  const [selectedCcnl, setSelectedCcnl] = useState('');
  const [ccnlVerification, setCcnlVerification] = useState(null);
  const [loadingCcnl, setLoadingCcnl] = useState(false);

  useEffect(() => {
    fetchPayslip();
    fetchCcnlList();
  }, [id]);
  
  const fetchCcnlList = async () => {
    try {
      const response = await axios.get(`${API_URL}/ccnl/list`);
      setCcnlList(response.data.ccnl || []);
    } catch (err) {
      console.error('Error fetching CCNL list:', err);
    }
  };
  
  const handleVerifyCcnl = async () => {
    if (!selectedCcnl) {
      toast.error('Seleziona un CCNL per la verifica avanzata');
      return;
    }
    
    setLoadingCcnl(true);
    try {
      const response = await axios.post(`${API_URL}/payslips/${id}/verify-ccnl?ccnl_id=${selectedCcnl}`);
      setCcnlVerification(response.data);
      toast.success('Verifica CCNL completata');
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore nella verifica CCNL';
      toast.error(message);
    } finally {
      setLoadingCcnl(false);
    }
  };

  const fetchPayslip = async () => {
    try {
      const response = await axios.get(`${API_URL}/payslips/${id}`);
      setPayslip(response.data);
    } catch (err) {
      toast.error('Busta paga non trovata');
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };

  const handleReverify = async () => {
    setReverifying(true);
    try {
      const response = await axios.get(`${API_URL}/payslips/${id}/reverify`);
      setPayslip(prev => ({ ...prev, verification: response.data }));
      toast.success('Verifica aggiornata');
    } catch (err) {
      toast.error('Errore nella verifica');
    } finally {
      setReverifying(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/payslips/${id}`);
      toast.success('Busta paga eliminata');
      navigate('/history');
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/payslips/${id}/export/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `busta_paga_${payslip.data.period_month}_${payslip.data.period_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF scaricato');
    } catch (err) {
      toast.error('Errore nel download del PDF');
    }
  };

  const handleSendNotification = async () => {
    try {
      await axios.post(`${API_URL}/notifications/send-anomaly`, {
        payslip_id: id
      });
      toast.success('Notifica email inviata!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore nell\'invio della notifica';
      toast.error(message);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (!payslip) return null;

  const { data, verification } = payslip;
  const isValid = verification.is_valid;

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="payslip-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                {MONTHS[data.period_month - 1]} {data.period_year}
              </h1>
              <p className="text-muted-foreground">{data.company_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="swiss-btn-secondary"
              onClick={handleExportPDF}
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            {!isValid && (
              <Button
                variant="outline"
                className="swiss-btn-secondary text-amber-600 border-amber-200 hover:bg-amber-50"
                onClick={handleSendNotification}
                data-testid="send-notification-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Invia Report
              </Button>
            )}
            <Button
              variant="outline"
              className="swiss-btn-secondary"
              onClick={handleReverify}
              disabled={reverifying}
              data-testid="reverify-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reverifying ? 'animate-spin' : ''}`} />
              Riverifica
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare la busta paga?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Questa azione non può essere annullata. La busta paga verrà eliminata definitivamente.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                    Elimina
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Verification Summary */}
        <Card className={`swiss-card ${isValid ? 'border-emerald-300 bg-emerald-50/30' : 'border-red-300 bg-red-50/30'}`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${isValid ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {isValid ? (
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                )}
              </div>
              <div className="flex-1">
                <h2 className={`text-lg font-semibold ${isValid ? 'text-emerald-800' : 'text-red-800'}`}>
                  {isValid ? 'Busta Paga Verificata' : `${verification.total_issues} Anomalie Rilevate`}
                </h2>
                <p className={`text-sm ${isValid ? 'text-emerald-700' : 'text-red-700'}`}>
                  {verification.summary}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Issues List */}
        {verification.issues.length > 0 && (
          <Card className="swiss-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Dettaglio Anomalie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {verification.issues.map((issue, index) => (
                <div
                  key={index}
                  className={`issue-${issue.severity}`}
                  data-testid={`issue-${index}`}
                >
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(issue.severity)}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{issue.message}</p>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <span>Atteso: <span className="font-mono">{formatCurrency(issue.expected)}</span></span>
                        <span>Rilevato: <span className="font-mono">{formatCurrency(issue.actual)}</span></span>
                        <span>Differenza: <span className={`font-mono ${issue.difference > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {issue.difference > 0 ? '+' : ''}{formatCurrency(issue.difference)}
                        </span></span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* CCNL Advanced Verification Section */}
        <Card className="swiss-card border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              Verifica Avanzata CCNL
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Confronta la tua busta paga con le tabelle retributive del tuo CCNL per verificare livello, mansione, scatti di anzianità e maggiorazioni.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Select value={selectedCcnl} onValueChange={setSelectedCcnl}>
                  <SelectTrigger className="swiss-input" data-testid="ccnl-select">
                    <SelectValue placeholder="Seleziona il tuo CCNL" />
                  </SelectTrigger>
                  <SelectContent>
                    {ccnlList.map((ccnl) => (
                      <SelectItem key={ccnl.id} value={ccnl.id}>
                        {ccnl.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={handleVerifyCcnl}
                disabled={!selectedCcnl || loadingCcnl}
                className="swiss-btn-primary"
                data-testid="verify-ccnl-btn"
              >
                {loadingCcnl ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifica...
                  </>
                ) : (
                  <>
                    <Scale className="w-4 h-4 mr-2" />
                    Verifica CCNL
                  </>
                )}
              </Button>
            </div>
            
            {/* CCNL Verification Results */}
            {ccnlVerification && (
              <div className="mt-6">
                <CCNLVerificationResult verification={ccnlVerification} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabella Confronto Dettagliata */}
        <PayslipComparisonTable 
          extractedData={data}
          ccnlData={ccnlVerification?.calculations?.livello_verifica || {}}
        />

        {/* Calculated Values */}
        <Card className="swiss-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5 text-forest" />
              Valori Calcolati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(verification.calculated_values).map(([key, value]) => (
                <div key={key} className="p-4 bg-slate-50 rounded-sm">
                  <p className="label-uppercase mb-1">
                    {key.replace(/_/g, ' ').replace('expected', 'Atteso').replace('taxable income', 'Imponibile').replace('total deductions', 'Tot. Trattenute').replace('irpef rate', 'Aliquota IRPEF').replace('gross from components', 'Lordo da Componenti')}
                  </p>
                  <p className="font-mono text-lg text-forest">
                    {typeof value === 'number' && key !== 'irpef_rate' ? formatCurrency(value) : 
                     key === 'irpef_rate' ? `${(value * 100).toFixed(0)}%` : value}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Payslip Data */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Anagrafica */}
          <Card className="swiss-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Dati Anagrafici</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-muted-foreground text-sm">Dipendente</span>
                <span className="font-medium text-sm">{data.employee_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-muted-foreground text-sm">Codice Fiscale</span>
                <span className="font-mono text-sm">{data.fiscal_code}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-muted-foreground text-sm">Azienda</span>
                <span className="font-medium text-sm">{data.company_name}</span>
              </div>
            </CardContent>
          </Card>

          {/* Retribuzione */}
          <Card className="swiss-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Retribuzione</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-muted-foreground text-sm">Paga Base</span>
                <span className="font-mono text-sm">{formatCurrency(data.base_salary)}</span>
              </div>
              {data.contingency > 0 && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-muted-foreground text-sm">Contingenza</span>
                  <span className="font-mono text-sm">{formatCurrency(data.contingency)}</span>
                </div>
              )}
              {data.overtime_amount > 0 && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-muted-foreground text-sm">Straordinario ({data.overtime_hours}h)</span>
                  <span className="font-mono text-sm">{formatCurrency(data.overtime_amount)}</span>
                </div>
              )}
              {data.bonuses > 0 && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-muted-foreground text-sm">Premi/Bonus</span>
                  <span className="font-mono text-sm">{formatCurrency(data.bonuses)}</span>
                </div>
              )}
              <div className="flex justify-between py-2 bg-mint -mx-6 px-6">
                <span className="font-semibold text-sm">Lordo Totale</span>
                <span className="font-mono font-semibold text-sm text-forest">{formatCurrency(data.gross_salary)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Trattenute */}
          <Card className="swiss-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Trattenute</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-muted-foreground text-sm">INPS Dipendente</span>
                <span className="font-mono text-sm text-red-600">-{formatCurrency(data.inps_employee)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-muted-foreground text-sm">IRPEF</span>
                <span className="font-mono text-sm text-red-600">-{formatCurrency(data.irpef)}</span>
              </div>
              {data.regional_tax > 0 && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-muted-foreground text-sm">Add. Regionale</span>
                  <span className="font-mono text-sm text-red-600">-{formatCurrency(data.regional_tax)}</span>
                </div>
              )}
              {data.municipal_tax > 0 && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-muted-foreground text-sm">Add. Comunale</span>
                  <span className="font-mono text-sm text-red-600">-{formatCurrency(data.municipal_tax)}</span>
                </div>
              )}
              {data.other_deductions > 0 && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-muted-foreground text-sm">Altre Trattenute</span>
                  <span className="font-mono text-sm text-red-600">-{formatCurrency(data.other_deductions)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Netto */}
          <Card className="swiss-card bg-forest text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-white/80">Netto in Busta</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-mono text-4xl font-bold">{formatCurrency(data.net_salary)}</p>
              {data.tfr_accrued > 0 && (
                <p className="mt-4 text-sm text-white/70">
                  TFR maturato: <span className="font-mono">{formatCurrency(data.tfr_accrued)}</span>
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
